import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { User, Save, Loader2, Phone, MapPin, Heart, Building2, CreditCard, FileText, Upload, Trash2, Plus, X, Camera, Calendar } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { value: 'aadhar_card', label: 'Aadhar Card' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'passport_copy', label: 'Passport' },
  { value: 'graduation_marksheet', label: 'Graduation Marksheet' },
  { value: 'graduation_degree', label: 'Graduation Original Degree' },
  { value: 'pg_marksheet', label: 'PG Mark List' },
  { value: 'pg_degree', label: 'PG Original Degree' },
  { value: 'phd_degree', label: 'PhD Original Degree' },
  { value: 'relieving_letter', label: 'Previous Employment Relieving Letter' },
];

const ProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // Documents
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('');
  const [docFile, setDocFile] = useState(null);
  // For multiple relieving letters
  const [extraRelievingLetters, setExtraRelievingLetters] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const [profRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/profile/me`, { withCredentials: true }),
        axios.get(`${API_URL}/api/profile/documents`, { withCredentials: true }),
      ]);
      setEmployee(profRes.data.employee);
      setProfile(profRes.data.profile);
      setForm(profRes.data.profile || {});
      setDocuments(docsRes.data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/profile/me`, form, { withCredentials: true });
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleUploadDoc = async (type, file, label) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const docLabel = label || DOCUMENT_TYPES.find(d => d.value === type)?.label || type;
      await axios.post(
        `${API_URL}/api/profile/documents?doc_type=${type}&doc_label=${encodeURIComponent(docLabel)}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`${docLabel} uploaded successfully`);
      setDocFile(null);
      setDocType('');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API_URL}/api/profile/documents/${docId}`, { withCredentials: true });
      toast.success('Document removed');
      fetchProfile();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const handleAddRelievingLetter = () => {
    setExtraRelievingLetters(prev => [...prev, { id: Date.now(), file: null, label: '' }]);
  };

  const handleRelievingFileChange = (id, file) => {
    setExtraRelievingLetters(prev => prev.map(r => r.id === id ? { ...r, file } : r));
  };

  const handleRelievingLabelChange = (id, label) => {
    setExtraRelievingLetters(prev => prev.map(r => r.id === id ? { ...r, label } : r));
  };

  const handleUploadRelieving = async (item) => {
    if (!item.file) return;
    const label = item.label || `Relieving Letter - ${new Date().toLocaleDateString()}`;
    await handleUploadDoc('relieving_letter', item.file, label);
    setExtraRelievingLetters(prev => prev.filter(r => r.id !== item.id));
  };

  const getDocsByType = (type) => documents.filter(d => d.doc_type === type);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be less than 5MB'); return; }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/profile/photo`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded successfully');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const getPhotoUrl = () => {
    const path = employee?.profile_picture || profile?.profile_picture;
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}/api/files/${path}`;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><User className="w-7 h-7 text-primary" />My Profile</h1>
        <p className="text-muted-foreground">View and update your personal details</p>
      </div>

      {/* Profile Photo + Employment Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" /> Employment Details</CardTitle>
          <CardDescription>These are managed by HR and cannot be edited</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6 mb-4">
            <div className="relative group">
              <Avatar className="h-20 w-20">
                <AvatarImage src={getPhotoUrl()} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                  {getInitials(user?.name || employee?.name)}
                </AvatarFallback>
              </Avatar>
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-testid="photo-upload-label">
                {uploadingPhoto ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload-input" />
              </label>
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold">{user?.name || employee?.name || '—'}</h2>
              <p className="text-sm text-muted-foreground">{employee?.designation || '—'} — {employee?.department || user?.department || '—'}</p>
              <p className="text-xs text-muted-foreground mt-1">Hover photo to update</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-muted-foreground text-xs">Email</p><p className="font-medium">{user?.email || employee?.email || '—'}</p></div>
            <div><p className="text-muted-foreground text-xs">Role</p><Badge variant="outline" className="capitalize">{user?.role || '—'}</Badge></div>
            <div><p className="text-muted-foreground text-xs">Employee ID</p><p className="font-medium">{employee?.employee_id || '—'}</p></div>
            <div><p className="text-muted-foreground text-xs">Joining Date</p><p className="font-medium">{employee?.date_of_joining || employee?.created_at?.slice(0, 10) || '—'}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Phone className="w-4 h-4" /> Contact Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Phone Number</Label><Input value={form.phone || ''} onChange={(e) => updateField('phone', e.target.value)} placeholder="+91 9876543210" data-testid="profile-phone" /></div>
            <div className="space-y-2"><Label>Blood Group</Label><Input value={form.blood_group || ''} onChange={(e) => updateField('blood_group', e.target.value)} placeholder="O+" data-testid="profile-blood-group" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Dates */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4" /> Personal Dates</CardTitle><CardDescription>Used for birthday wishes and anniversary greetings from the founder</CardDescription></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth || ''} onChange={(e) => updateField('date_of_birth', e.target.value)} data-testid="profile-dob" /></div>
            <div className="space-y-2">
              <Label>Marital Status</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.marital_status || ''} onChange={(e) => updateField('marital_status', e.target.value)} data-testid="profile-marital-status">
                <option value="">Select</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
              </select>
            </div>
            {form.marital_status === 'married' && (
              <div className="space-y-2"><Label>Date of Marriage</Label><Input type="date" value={form.date_of_marriage || ''} onChange={(e) => updateField('date_of_marriage', e.target.value)} data-testid="profile-dom" /></div>
            )}
          </div>
          {/* Kids */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label>Kids</Label>
              <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => updateField('kids', [...(form.kids || []), { name: '', dob: '' }])} data-testid="add-kid-btn"><Plus className="w-3 h-3" /> Add Kid</Button>
            </div>
            {(form.kids || []).map((kid, idx) => (
              <div key={idx} className="flex items-end gap-2 mb-2">
                <div className="flex-1 space-y-1"><Label className="text-xs">Name</Label><Input value={kid.name || ''} onChange={(e) => { const kids = [...(form.kids || [])]; kids[idx] = { ...kids[idx], name: e.target.value }; updateField('kids', kids); }} placeholder="Kid's name" data-testid={`kid-name-${idx}`} /></div>
                <div className="flex-1 space-y-1"><Label className="text-xs">Date of Birth</Label><Input type="date" value={kid.dob || ''} onChange={(e) => { const kids = [...(form.kids || [])]; kids[idx] = { ...kids[idx], dob: e.target.value }; updateField('kids', kids); }} data-testid={`kid-dob-${idx}`} /></div>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => { const kids = (form.kids || []).filter((_, i) => i !== idx); updateField('kids', kids); }}><X className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4" /> Address</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2"><Label>Address</Label><Input value={form.address || ''} onChange={(e) => updateField('address', e.target.value)} placeholder="Flat/House No, Street, Area" data-testid="profile-address" /></div>
            <div className="space-y-2"><Label>City</Label><Input value={form.city || ''} onChange={(e) => updateField('city', e.target.value)} placeholder="Hyderabad" data-testid="profile-city" /></div>
            <div className="space-y-2"><Label>State</Label><Input value={form.state || ''} onChange={(e) => updateField('state', e.target.value)} placeholder="Telangana" data-testid="profile-state" /></div>
            <div className="space-y-2"><Label>Pincode</Label><Input value={form.pincode || ''} onChange={(e) => updateField('pincode', e.target.value)} placeholder="500081" data-testid="profile-pincode" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Heart className="w-4 h-4" /> Emergency Contact</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2"><Label>Name</Label><Input value={form.emergency_contact_name || ''} onChange={(e) => updateField('emergency_contact_name', e.target.value)} placeholder="Contact name" data-testid="profile-emergency-name" /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={form.emergency_contact_phone || ''} onChange={(e) => updateField('emergency_contact_phone', e.target.value)} placeholder="+91 9876543210" data-testid="profile-emergency-phone" /></div>
            <div className="space-y-2"><Label>Relation</Label><Input value={form.emergency_contact_relation || ''} onChange={(e) => updateField('emergency_contact_relation', e.target.value)} placeholder="Spouse / Parent" data-testid="profile-emergency-relation" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Bank & ID */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-4 h-4" /> Bank & ID Details</CardTitle>
          <CardDescription>Used for salary processing and compliance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Bank Name</Label><Input value={form.bank_name || ''} onChange={(e) => updateField('bank_name', e.target.value)} placeholder="HDFC Bank" data-testid="profile-bank-name" /></div>
            <div className="space-y-2"><Label>Account Number</Label><Input value={form.bank_account_number || ''} onChange={(e) => updateField('bank_account_number', e.target.value)} placeholder="1234567890" data-testid="profile-account-number" /></div>
            <div className="space-y-2"><Label>IFSC Code</Label><Input value={form.bank_ifsc || ''} onChange={(e) => updateField('bank_ifsc', e.target.value)} placeholder="HDFC0001234" data-testid="profile-ifsc" /></div>
            <div className="space-y-2"><Label>PAN Number</Label><Input value={form.pan_number || ''} onChange={(e) => updateField('pan_number', e.target.value)} placeholder="ABCDE1234F" data-testid="profile-pan" /></div>
            <div className="space-y-2"><Label>Aadhar Number</Label><Input value={form.aadhar_number || ''} onChange={(e) => updateField('aadhar_number', e.target.value)} placeholder="1234 5678 9012" data-testid="profile-aadhar" /></div>
            <div className="space-y-2"><Label>UAN Number (for PF)</Label><Input value={form.uan_number || ''} onChange={(e) => updateField('uan_number', e.target.value)} placeholder="100000000106" data-testid="profile-uan" /></div>
            <div className="space-y-2"><Label>PF Member ID</Label><Input value={form.pf_member_id || ''} onChange={(e) => updateField('pf_member_id', e.target.value)} placeholder="KNRPF00123456700001234" data-testid="profile-pf-member-id" /></div>
            <div className="space-y-2"><Label>Medical Insurance Card Number</Label><Input value={form.medical_insurance_card_number || ''} onChange={(e) => updateField('medical_insurance_card_number', e.target.value)} placeholder="EC2635769840-01E" data-testid="profile-medical-insurance-card" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Passport */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Passport Details</CardTitle>
          <CardDescription>Used for international travel & visa processing</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2"><Label>Passport Number</Label><Input value={form.passport_number || ''} onChange={(e) => updateField('passport_number', e.target.value.toUpperCase())} placeholder="A12345678" data-testid="profile-passport-number" /></div>
            <div className="space-y-2"><Label>Issue Date</Label><Input type="date" value={form.passport_issue_date || ''} onChange={(e) => updateField('passport_issue_date', e.target.value)} data-testid="profile-passport-issue" /></div>
            <div className="space-y-2"><Label>Expiry Date</Label><Input type="date" value={form.passport_expiry_date || ''} onChange={(e) => updateField('passport_expiry_date', e.target.value)} data-testid="profile-passport-expiry" /></div>
          </div>
          {form.passport_expiry_date && (() => {
            const today = new Date();
            const expiry = new Date(form.passport_expiry_date);
            const daysToExpiry = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
            if (daysToExpiry < 0) return <p className="text-xs text-destructive mt-2">Passport has expired. Please renew.</p>;
            if (daysToExpiry < 180) return <p className="text-xs text-amber-600 mt-2">Expires in {daysToExpiry} days. Plan renewal soon (most countries require 6+ months validity).</p>;
            return null;
          })()}
        </CardContent>
      </Card>

      {/* Save Profile */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-profile-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Profile
        </Button>
      </div>

      {/* Document Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Document Proofs</CardTitle>
          <CardDescription>Upload your identity and education documents. Accessible to you, SuperAdmin, and HR only.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Standard documents */}
          {DOCUMENT_TYPES.filter(d => d.value !== 'relieving_letter').map((dtype) => {
            const existingDocs = getDocsByType(dtype.value);
            return (
              <div key={dtype.value} className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/30 border border-border/50">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{dtype.label}</p>
                  {existingDocs.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {existingDocs.map(doc => (
                        <div key={doc.id} className="flex items-center gap-1.5">
                          <Badge variant="outline" className="gap-1 text-xs cursor-pointer text-primary hover:bg-primary/10" onClick={() => window.open(`${API_URL}/api/files/${doc.storage_path}`, '_blank')}>
                            <FileText className="w-3 h-3" /> {doc.original_filename}
                          </Badge>
                          <button onClick={() => handleDeleteDoc(doc.id)} className="text-destructive hover:text-destructive/80">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5">Not uploaded</p>
                  )}
                </div>
                <label className="cursor-pointer shrink-0">
                  <Button size="sm" variant="outline" className="gap-1.5" asChild>
                    <span><Upload className="w-3.5 h-3.5" />{existingDocs.length > 0 ? 'Replace' : 'Upload'}</span>
                  </Button>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" data-testid={`upload-${dtype.value}`}
                    onChange={(e) => { if (e.target.files[0]) handleUploadDoc(dtype.value, e.target.files[0], dtype.label); e.target.value = ''; }}
                  />
                </label>
              </div>
            );
          })}

          {/* Relieving Letters (multiple) */}
          <div className="p-3 rounded-lg bg-muted/30 border border-border/50 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Previous Employment Relieving Letters</p>
                <p className="text-xs text-muted-foreground">Upload one per previous employer</p>
              </div>
              <Button size="sm" variant="outline" className="gap-1" onClick={handleAddRelievingLetter} data-testid="add-relieving-letter">
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
            </div>

            {/* Existing relieving letters */}
            {getDocsByType('relieving_letter').map(doc => (
              <div key={doc.id} className="flex items-center gap-2 pl-2">
                <Badge variant="outline" className="gap-1 text-xs cursor-pointer text-primary hover:bg-primary/10" onClick={() => window.open(`${API_URL}/api/files/${doc.storage_path}`, '_blank')}>
                  <FileText className="w-3 h-3" /> {doc.doc_label || doc.original_filename}
                </Badge>
                <button onClick={() => handleDeleteDoc(doc.id)} className="text-destructive hover:text-destructive/80">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {/* New relieving letter uploads */}
            {extraRelievingLetters.map(item => (
              <div key={item.id} className="flex items-center gap-2 pl-2">
                <Input
                  value={item.label}
                  onChange={(e) => handleRelievingLabelChange(item.id, e.target.value)}
                  placeholder="e.g., TCS Relieving Letter"
                  className="flex-1 h-9 text-sm"
                />
                <label className="cursor-pointer">
                  <Button size="sm" variant="outline" className="gap-1" asChild>
                    <span><Upload className="w-3.5 h-3.5" />{item.file ? item.file.name.slice(0, 15) : 'Choose file'}</span>
                  </Button>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => { if (e.target.files[0]) handleRelievingFileChange(item.id, e.target.files[0]); }} />
                </label>
                <Button size="sm" onClick={() => handleUploadRelieving(item)} disabled={!item.file || uploading} className="gap-1">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
                </Button>
                <button onClick={() => setExtraRelievingLetters(prev => prev.filter(r => r.id !== item.id))} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {uploading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading document...
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
